
import React, { useState } from 'react';
import { Card, CardHeader, CardTitle, CardDescription, CardContent } from '@/components/ui/card';
import { useUser } from '@/context/UserContext';
import { Activity, Moon, Brain } from 'lucide-react';
import { toast } from 'sonner';
import { Accordion } from '@/components/ui/accordion';
import { defaultHealthData, HealthData } from './HealthAssessmentData';
import AccordionSection from './sections/AccordionSection';
import PersonalInfoSection from './sections/PersonalInfoSection';
import SymptomsSection from './sections/SymptomsSection';
import SleepSection from './sections/SleepSection';
import MedicalHistorySection from './sections/MedicalHistorySection';

const HealthAssessmentTab = () => {
  const { user } = useUser();
  const [editMode, setEditMode] = useState<string | null>(null);
  
  // In a real app this would come from the user's profile
  // In a production environment, this data would be fetched from the backend
  const [healthData, setHealthData] = useState<HealthData>(defaultHealthData);

  const handleEditToggle = (section: string) => {
    if (editMode === section) {
      setEditMode(null);
    } else {
      setEditMode(section);
    }
  };

  const handleSave = (section: string, data: any) => {
    setHealthData(prev => ({
      ...prev,
      [section]: data
    }));
    setEditMode(null);
    toast.success(`${section} information updated successfully`);
  };

  return (
    <Card>
      <CardHeader>
        <CardTitle>Health Assessment</CardTitle>
        <CardDescription>
          View and edit your health assessment information
        </CardDescription>
      </CardHeader>
      <CardContent className="space-y-6">
        <Accordion type="single" collapsible className="w-full">
          {/* Personal Information Section */}
          <AccordionSection 
            value="personal-info" 
            icon={<Activity className="h-5 w-5 text-[#e27d5f]" />}
            title="Personal Information"
          >
            <PersonalInfoSection 
              data={healthData.personalInfo}
              editMode={editMode}
              onEditToggle={handleEditToggle}
              onSave={handleSave}
            />
          </AccordionSection>

          {/* Symptoms Section */}
          <AccordionSection 
            value="symptoms" 
            icon={<Activity className="h-5 w-5 text-[#e27d5f]" />}
            title="Symptoms Assessment"
          >
            <SymptomsSection 
              data={healthData.symptoms}
              editMode={editMode}
              onEditToggle={handleEditToggle}
              onSave={handleSave}
            />
          </AccordionSection>

          {/* Sleep Assessment Section */}
          <AccordionSection 
            value="sleep" 
            icon={<Moon className="h-5 w-5 text-[#e27d5f]" />}
            title="Sleep Assessment"
          >
            <SleepSection 
              data={healthData.sleepAssessment}
              editMode={editMode}
              onEditToggle={handleEditToggle}
              onSave={handleSave}
            />
          </AccordionSection>

          {/* Medical History Section */}
          <AccordionSection 
            value="medical" 
            icon={<Brain className="h-5 w-5 text-[#e27d5f]" />}
            title="Medical History"
          >
            <MedicalHistorySection 
              data={healthData.medicalHistory}
              editMode={editMode}
              onEditToggle={handleEditToggle}
              onSave={handleSave}
            />
          </AccordionSection>
        </Accordion>
      </CardContent>
    </Card>
  );
};

export default HealthAssessmentTab;
